/* ==========================================================================
   Blenders Online 2026 · bo-hero.js · the home page's photo slider (sections/bo-hero.liquid)
   No dependencies. Loaded deferred by the section; a second load only re-binds. Idempotent, re-binds on
   shopify:section:load, lets go on shopify:section:unload, follows the theme editor's block selection.

   Autoplay (data-autoplay="true", --bo-hero-dur seconds a slide) holds while: a mouse is over the slides,
   keyboard focus is inside, a finger is on it, it is scrolled off screen, the tab is hidden, a block is selected
   in the editor. A swipe hands the slider to the shopper: it stops moving by itself (phones have no pause
   button, by the owner's rule). prefers-reduced-motion: no autoplay and instant changes.
   No later slide may become Chrome's LCP (fix-seo 02-10): Chrome times the page's largest paint until the first
   input, and slide 1's 3:1 desktop crop counts as slightly upscaled, so slide 2 shown in full counted as larger and
   the desktop LCP became the moment of the first change (7.1-7.5 s instead of about 1 s). Chrome counts a photo at
   the size it is FIRST painted, so before a slide is shown for the first time its photo is decoded and painted
   once as a 1px strip behind the slide on show (.is-prepaint, two frames, bo-hero.css): it is on record as tiny,
   and showing it in full is not a new candidate. The autoplay runs from the start (Tommy 02-10: "i want the
   images to change before scroll").
   Slides 2+ keep their photo in data-src / data-srcset until slide 1's photo has arrived (the next one) or the
   page has loaded (the rest); autoplay never moves on to a photo that has not arrived.
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.BOHero) { W.BOHero.init(D); return; }

  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const heroes = new WeakMap();
  const idle = (fn) => (W.requestIdleCallback ? W.requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 800));

  const hydrate = (media) => {
    if (!media || !media.hasAttribute('data-bo-hero-lazy')) return;
    media.removeAttribute('data-bo-hero-lazy');
    media.querySelectorAll('source[data-srcset]').forEach((el) => { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); });
    media.querySelectorAll('img').forEach((el) => {
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
      if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
    });
  };
  const imgOf = (m) => (m ? m.querySelector('img') : null);
  const ready = (m) => { const im = imgOf(m); return !im || (!m.hasAttribute('data-bo-hero-lazy') && im.complete && im.naturalWidth > 0); };

  const setup = (root) => {
    if (heroes.has(root)) return;
    const stage = root.querySelector('[data-bo-hero-stage]') || root;
    const slides = [...root.querySelectorAll('[data-bo-hero-slide]')];
    const medias = slides.map((s) => s.querySelector('.bo-hero__media'));
    const tabs = [...root.querySelectorAll('[data-bo-hero-tab]')];
    const live = root.querySelector('[data-bo-hero-live]');
    const n = slides.length;
    const ac = new AbortController();
    const on = (el, ev, fn, opt = {}) => el && el.addEventListener(ev, fn, { ...opt, signal: ac.signal });
    const st = { hover: false, focus: false, touch: false, stopped: false, off: false, hidden: D.hidden, editor: false };
    let i = Math.max(0, slides.findIndex((el) => el.classList.contains('is-active')));
    let timer = 0, due = 0, left = 0, cleanT = 0, waitT = 0, entering = null;

    // read once (a style read on every slide change forced a style recalculation); the editor re-binds the section
    const durMs = (parseFloat(W.getComputedStyle(root).getPropertyValue('--bo-hero-dur')) || 6) * 1000;
    const dur = () => durMs;
    const auto = () => root.dataset.autoplay === 'true' && n > 1 && !reduce.matches;
    const held = () => st.hover || st.focus || st.touch || st.stopped || st.off || st.hidden || st.editor;
    left = dur();

    // the clock: counts only while nothing holds it, and keeps what was left of a slide's time across a hold
    const stopClock = () => { if (timer) { clearTimeout(timer); timer = 0; left = Math.max(400, due - performance.now()); } };
    const runClock = () => { if (timer || !auto() || held()) return; due = performance.now() + left; timer = setTimeout(advance, left); };
    const resetClock = () => { clearTimeout(timer); timer = 0; left = dur(); };

    const sync = () => {
      const a = auto();
      root.classList.toggle('is-held', held());
      if (live) live.setAttribute('aria-live', a && !st.stopped ? 'off' : 'polite');
      if (a && !held()) runClock(); else stopClock();
    };

    const go = (to, focusTab) => {
      to = (to + n) % n;
      if (to === i || n < 2) return;
      hydrate(medias[to]);
      hydrate(medias[(to + 1) % n]);
      const prev = slides[i], next = slides[to];
      clearTimeout(cleanT);
      slides.forEach((s) => s.classList.remove('is-leaving', 'is-entering'));
      if (entering && entering !== slides[to]) entering = null;
      prev.classList.remove('is-active');
      prev.setAttribute('inert', '');
      next.classList.add('is-active');
      next.removeAttribute('inert');
      if (!reduce.matches) {
        prev.classList.add('is-leaving');
        // fix-home 01-10 (critic 19): no forced layout ('void offsetWidth') on a normal change. The entering class
        // was cleared 1.75 s after the last change, so adding it starts the bloom fresh. Only a change back to a
        // slide still mid-bloom needs a restart: that one waits a frame instead of forcing a layout.
        if (entering === next) {
          next.style.visibility = 'hidden';
          requestAnimationFrame(() => requestAnimationFrame(() => { next.style.visibility = ''; next.classList.add('is-entering'); }));
        } else {
          next.classList.add('is-entering');
        }
        entering = next;
        cleanT = setTimeout(() => { prev.classList.remove('is-leaving'); next.classList.remove('is-entering'); entering = null; }, 1750);
      }
      tabs.forEach((t, k) => {
        const sel = k === to;
        t.setAttribute('aria-selected', sel ? 'true' : 'false');
        t.tabIndex = sel ? 0 : -1;
      });
      if (focusTab && tabs[to]) tabs[to].focus();
      i = to;
      resetClock();
      sync();
    };

    // a slide's photo painted once as a 1px strip behind the slide on show, before it is first shown (see the top):
    // decoded first, so that paint really draws it; two frames, then hidden again
    const painted = new WeakSet();
    const prepaint = (k, then) => {
      const s = slides[k], im = imgOf(medias[k]);
      if (!s || !im || painted.has(s) || k === i || reduce.matches) { then(); return; }
      painted.add(s);
      const strip = () => {
        s.classList.add('is-prepaint');
        requestAnimationFrame(() => requestAnimationFrame(() => { s.classList.remove('is-prepaint'); then(); }));
      };
      (im.decode ? im.decode() : Promise.resolve()).then(strip, strip);
    };

    // autoplay moves on only once the next photo has arrived (a slow phone waits rather than show a blank)
    function advance() {
      timer = 0;
      const nx = (i + 1) % n, from = i;
      hydrate(medias[nx]);
      const step = () => {
        if (i !== from || !auto()) return;
        if (held()) { runClock(); return; }        // held when it arrived: the clock takes over again on release
        go(nx);
      };
      const move = () => { clearTimeout(waitT); prepaint(nx, step); };
      if (ready(medias[nx])) { move(); return; }
      const im = imgOf(medias[nx]);
      if (im) { on(im, 'load', move, { once: true }); on(im, 'error', move, { once: true }); }
      waitT = setTimeout(move, 5000);
    }

    // photos 2+: the next one as soon as slide 1's has arrived, the rest once the page has loaded
    const warmNext = () => hydrate(medias[(i + 1) % n]);
    const first = imgOf(medias[i]);
    if (!first || (first.complete && first.naturalWidth > 0)) warmNext(); else on(first, 'load', warmNext, { once: true });
    const warmAll = () => idle(() => medias.forEach(hydrate));
    if (D.readyState === 'complete') warmAll(); else on(W, 'load', warmAll, { once: true });

    tabs.forEach((t, k) => {
      on(t, 'click', () => { go(k); });
      on(t, 'keydown', (e) => {
        const map = { ArrowRight: k + 1, ArrowDown: k + 1, ArrowLeft: k - 1, ArrowUp: k - 1, Home: 0, End: n - 1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        go(map[e.key], true);
      });
    });

    on(stage, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { st.hover = true; sync(); } });
    on(stage, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { st.hover = false; sync(); } });
    on(root, 'focusin', (e) => {
      let kb = true;
      try { kb = e.target.matches(':focus-visible'); } catch (err) { /* old browser: treat as keyboard */ }
      if (kb) { st.focus = true; sync(); }
    });
    on(root, 'focusout', (e) => { if (!root.contains(e.relatedTarget)) { st.focus = false; sync(); } });

    // touch: hold while a finger is down; a sideways swipe changes slide and stops the autoplay
    let x0 = null, y0 = 0;
    on(stage, 'touchstart', (e) => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; st.touch = true; sync(); }, { passive: true });
    const lift = (e) => {
      st.touch = false;
      if (x0 !== null && e.changedTouches && e.changedTouches[0]) {
        const t = e.changedTouches[0], dx = t.clientX - x0, dy = t.clientY - y0;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.3) { st.stopped = true; go(i + (dx < 0 ? 1 : -1)); }
      }
      x0 = null;
      sync();
    };
    on(stage, 'touchend', lift, { passive: true });
    on(stage, 'touchcancel', lift, { passive: true });

    let io = null;
    if ('IntersectionObserver' in W) {
      io = new IntersectionObserver((ens) => { st.off = !ens[0].isIntersecting; sync(); }, { threshold: 0.15 });
      io.observe(stage);
    }
    on(D, 'visibilitychange', () => { st.hidden = D.hidden; sync(); });
    on(reduce, 'change', sync);

    sync();
    root.classList.add('is-ready');

    heroes.set(root, {
      select(slide) { const k = slides.indexOf(slide); st.editor = true; if (k > -1) go(k); sync(); },
      deselect() { st.editor = false; sync(); },
      destroy() { ac.abort(); if (io) io.disconnect(); clearTimeout(timer); clearTimeout(cleanT); clearTimeout(waitT); heroes.delete(root); }
    });
  };

  const init = (scope) => {
    const list = scope.matches && scope.matches('[data-bo-hero]') ? [scope] : [...scope.querySelectorAll('[data-bo-hero]')];
    list.forEach(setup);
  };
  const find = (e) => {
    const root = e.target && e.target.closest ? e.target.closest('[data-bo-hero]') : null;
    return root ? heroes.get(root) : null;
  };
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-bo-hero]').forEach((r) => { const h = heroes.get(r); if (h) h.destroy(); });
  });
  D.addEventListener('shopify:block:select', (e) => {
    const h = find(e);
    const slide = e.target.closest && e.target.closest('[data-bo-hero-slide]');
    if (h && slide) h.select(slide);
  });
  D.addEventListener('shopify:block:deselect', (e) => { const h = find(e); if (h) h.deselect(); });

  W.BOHero = { init };
  // Loaded async after the slider's markup (sections/bo-hero.liquid), so the slider is already in the page: start it
  // now, not at DOMContentLoaded (which waits for every deferred script; about 11 s on a slow phone). The second
  // call only catches a slider printed later; setup() skips one already running.
  init(D);
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D), { once: true });
})();
